import React, { useEffect, useState } from 'react';
import { Clock, MapPin, Repeat, Bell, Users, User as UserIcon, ExternalLink, Info } from 'lucide-react';
import { Modal } from '../Modal';
import { ConfirmDialog } from '../ConfirmDialog';
import { ForwardTargetModal } from '../ForwardTargetModal';
import { useToast } from '../../contexts/ToastContext';
import { ApiError } from '../../services/apiClient';
import {
  CalendarDetailRequest, calendarApi, closeDetail, requestOpenCalendarAt, requestOpenEditor,
} from '../../services/calendarService';
import { CalendarEventDetail, CalendarResponse, Channel, Message, User } from '../../types';
import { dateKeyToMs, formatOccurrence, isValidDateKey, remindLabel } from '../../utils/calendarDate';
import { openExternalUrl } from '../../utils/openExternal';
import { SCOPE_STYLE, scopeLabelOf } from './scopeStyles';

interface EventDetailModalProps {
  request: CalendarDetailRequest & { seq: number };
  currentUser: User;
  channels: Channel[];
}

const RESPONSE_LABEL: Record<CalendarResponse, string> = { accepted: '참석', declined: '불참', pending: '응답 전' };
/**
 * 참석자는 응답별로 묶어 글자 머리로 보인다(예전엔 칩 색만 달라 무슨 뜻인지 알 수 없었다).
 * 칩은 모두 같은 중성색이고 불참만 흐리게 — 분류색·브랜드 레드는 쓰지 않는다.
 */
const RESPONSE_ORDER: CalendarResponse[] = ['accepted', 'pending', 'declined'];

/** 요청한 회차(반복 일정의 그 날짜)의 시작·끝 — 없으면 첫 회차 */
function occurrenceOf(ev: CalendarEventDetail, date?: string) {
  if (!ev.repeat || !date || !isValidDateKey(date) || date === ev.startDate) return { start: ev.start, end: ev.end, date: ev.startDate };
  const shift = dateKeyToMs(date) - dateKeyToMs(ev.startDate);
  return {
    start: new Date(new Date(ev.start).getTime() + shift).toISOString(),
    end: new Date(new Date(ev.end).getTime() + shift).toISOString(),
    date,
  };
}

/**
 * 일정 상세 — App 루트 CalendarModalsHost가 렌더한다(어디서 열든 같은 창).
 * 동작: 참석·불참(참석자 — 응답하면 창이 닫힌다) / 대화방에 공유 / 수정 / 삭제(아웃라인 — 파괴적 액션은 모양으로 구분) / 캘린더에서 보기.
 * 공유 메시지로 연 일정(viaMessage)은 읽기 전용이고, 볼 수 없으면 서버가 404를 준다.
 */
export const EventDetailModal: React.FC<EventDetailModalProps> = ({ request, currentUser, channels }) => {
  const { showToast } = useToast();
  const [event, setEvent] = useState<CalendarEventDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [recurringDelete, setRecurringDelete] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let ignore = false;
    setError(null);
    calendarApi.getEvent(request.eventId, request.viaMessageId)
      .then(ev => { if (!ignore) setEvent(ev); })
      .catch(err => {
        if (ignore) return;
        setError(err instanceof ApiError && err.status === 404
          ? '일정을 찾을 수 없습니다. 삭제됐거나 볼 수 없는 일정입니다.'
          : '일정을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
      });
    return () => { ignore = true; };
  }, [request.eventId, request.viaMessageId, request.seq, reloadTick]);

  const occ = event ? occurrenceOf(event, request.date) : null;

  // 응답하면 창을 닫는다(불참이면 일정이 내 캘린더에서 사라지기도 한다). 실패하면 닫지 않는다 — 다시 누를 수 있게
  const respond = async (response: CalendarResponse) => {
    if (!event) return;
    setBusy(true);
    try {
      await calendarApi.respond(event.id, response);
      showToast({ type: 'success', message: response === 'accepted' ? '참석으로 응답했습니다' : response === 'declined' ? '불참으로 응답했습니다' : '응답을 지웠습니다' });
      closeDetail();
    } catch {
      showToast({ type: 'error', message: '응답을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
      setBusy(false);
    }
  };

  const doDelete = async (mode: 'this' | 'following' | 'all') => {
    if (!event) return;
    setBusy(true);
    try {
      await calendarApi.remove(event.id, mode, occ?.date);
      showToast({ type: 'success', message: '삭제했습니다' });
      closeDetail();
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error && err.message ? err.message : '일정을 삭제하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } finally {
      setBusy(false);
      setConfirmDelete(false);
      setRecurringDelete(false);
    }
  };

  const doShare = async (channelIds: number[]) => {
    if (!event) return;
    setSharing(false);
    try {
      const results = await calendarApi.share(event.id, channelIds, request.viaMessageId);
      const okCount = results.filter(r => r.ok).length;
      if (okCount === results.length) showToast({ type: 'success', message: '공유했습니다' });
      else if (okCount > 0) showToast({ type: 'error', message: `${results.length}곳 중 ${okCount}곳에 공유했습니다. ${results.find(r => !r.ok)?.error || ''}`.trim() });
      else showToast({ type: 'error', message: results[0]?.error || '일정을 공유하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error && err.message ? err.message : '일정을 공유하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    }
  };

  const openInCalendar = () => {
    if (!event || !occ) return;
    closeDetail();
    requestOpenCalendarAt({ date: occ.date, eventId: event.id });
  };

  const row = (icon: React.ReactNode, content: React.ReactNode, key: string) => (
    <div key={key} className="flex items-start gap-3 py-1.5">
      <span className="w-5 shrink-0 pt-0.5 text-slate-400 flex justify-center" aria-hidden="true">{icon}</span>
      <div className="flex-1 min-w-0 text-sm text-slate-700">{content}</div>
    </div>
  );

  const footer = event ? (
    <div className="flex flex-wrap items-center gap-2">
      {event.canRespond && (
        <div className="flex gap-1.5 mr-auto" role="group" aria-label="참석 응답">
          {(['accepted', 'declined'] as CalendarResponse[]).map(r => (
            <button
              key={r}
              type="button"
              disabled={busy}
              aria-pressed={event.myResponse === r}
              onClick={() => respond(event.myResponse === r ? 'pending' : r)}
              className={`h-9 px-3 rounded-lg border text-sm font-semibold transition-colors disabled:opacity-50 ${
                event.myResponse === r ? 'bg-brand-600 border-brand-600 text-white hover:bg-brand-700' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              {RESPONSE_LABEL[r]}
            </button>
          ))}
        </div>
      )}
      {!event.canRespond && <span className="mr-auto" />}
      {!request.fromCalendar && event.source === 'chat' && (
        <button type="button" onClick={openInCalendar} className="h-9 px-3 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">
          캘린더에서 보기
        </button>
      )}
      {event.canShare && (
        <button type="button" onClick={() => setSharing(true)} className="h-9 px-3 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">
          대화방에 공유
        </button>
      )}
      {event.canDelete && (
        <button
          type="button"
          disabled={busy}
          onClick={() => (event.repeat ? setRecurringDelete(true) : setConfirmDelete(true))}
          className="h-9 px-3 rounded-lg bg-white border border-brand-600 text-brand-600 hover:bg-brand-50 text-sm font-semibold disabled:opacity-50"
        >
          삭제
        </button>
      )}
      {event.canEdit && (
        <button
          type="button"
          disabled={busy}
          onClick={() => { closeDetail(); requestOpenEditor({ event }); }}
          className="h-9 px-4 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-bold disabled:opacity-50"
        >
          수정
        </button>
      )}
    </div>
  ) : undefined;

  const style = event ? (event.source === 'intranet' ? SCOPE_STYLE.dept : SCOPE_STYLE[event.scope]) : null;

  return (
    <>
      <Modal isOpen onClose={closeDetail} title={event ? event.title : error ? '일정' : '일정 불러오는 중…'} footer={footer} size="md">
        {error ? (
          <div className="py-4">
            <p className="text-sm font-semibold text-slate-700">{error}</p>
            <button
              type="button"
              onClick={() => setReloadTick(n => n + 1)}
              className="mt-3 h-9 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              다시 시도
            </button>
          </div>
        ) : !event || !occ || !style ? (
          <div className="py-6 space-y-2" aria-busy="true">
            <div className="h-4 w-2/3 rounded bg-slate-100 animate-pulse" />
            <div className="h-4 w-1/2 rounded bg-slate-100 animate-pulse" />
          </div>
        ) : (
          <div className="flex flex-col">
            <div className="mb-2 flex items-center gap-1.5">
              <span className={`inline-flex items-center gap-1.5 h-6 px-2 rounded-full text-xs font-semibold ${event.source === 'intranet' ? 'bg-white text-emerald-800 border border-dashed border-emerald-400' : style.chip}`}>
                <span aria-hidden="true" className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
                {scopeLabelOf(event, event.deptName)}
              </span>
              {event.viaMessage && <span className="text-xs text-slate-500">대화방에서 공유된 일정 · 보기 전용</span>}
            </div>
            {row(<Clock size={16} />, formatOccurrence(occ.start, occ.end, event.allDay), 'when')}
            {event.repeat && row(<Repeat size={16} />, event.repeatSummary || '반복', 'repeat')}
            {event.source === 'chat' && event.remindOffset !== null && row(<Bell size={16} />, remindLabel(event.remindOffset, event.allDay), 'remind')}
            {event.location && row(<MapPin size={16} />, <span className="break-anywhere">{event.location}</span>, 'loc')}
            {event.ownerName && row(<UserIcon size={16} />, `${event.ownerName} 님이 등록`, 'owner')}
            {event.attendees.length > 0 && row(
              <Users size={16} />,
              <div className="space-y-1.5">
                {RESPONSE_ORDER.map(r => {
                  const group = event.attendees.filter(a => a.response === r);
                  if (group.length === 0) return null;
                  return (
                    <div key={r} className="flex items-start gap-2">
                      <span className="w-[4.5rem] shrink-0 whitespace-nowrap pt-1.5 text-xs font-semibold text-slate-500" aria-hidden="true">
                        {RESPONSE_LABEL[r]} ({group.length})
                      </span>
                      <ul className="flex-1 min-w-0 flex flex-wrap gap-1.5" aria-label={`${RESPONSE_LABEL[r]} ${group.length}명`}>
                        {group.map(a => (
                          <li
                            key={a.userId}
                            className={`h-7 px-2.5 rounded-full bg-slate-100 text-[13px] font-medium flex items-center ${r === 'declined' ? 'text-slate-500' : 'text-slate-700'}`}
                          >
                            {a.name || a.userId}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>,
              'att',
            )}
            {event.description && (
              <p className="mt-2 pt-3 border-t border-slate-100 text-sm text-slate-700 whitespace-pre-wrap break-anywhere">{event.description}</p>
            )}
            {event.source === 'intranet' && (
              <div className="mt-3 flex items-start gap-1.5 text-xs text-slate-500">
                <Info size={13} className="shrink-0 mt-px" aria-hidden="true" />
                <span>인트라넷 '부서별일정'에 등록된 일정입니다. 수정·삭제는 인트라넷에서 할 수 있습니다.</span>
              </div>
            )}
            {event.source === 'intranet' && event.intranetUrl && (
              <button
                type="button"
                onClick={() => openExternalUrl(event.intranetUrl as string)}
                className="mt-2 self-start h-9 px-3 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 flex items-center gap-1.5"
              >
                <ExternalLink size={14} aria-hidden="true" />
                인트라넷에서 열기
              </button>
            )}
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={confirmDelete}
        title="일정 삭제"
        message={event ? `'${event.title}' 일정을 삭제할까요?${event.attendees.length > 0 ? ' 참석자의 캘린더에서도 사라집니다.' : event.scope === 'dept' ? ' 부서원 모두의 캘린더에서 사라집니다.' : event.scope === 'company' ? ' 직원 모두의 캘린더에서 사라집니다.' : ''}` : ''}
        confirmLabel="삭제"
        danger
        onConfirm={() => doDelete('all')}
        onCancel={() => setConfirmDelete(false)}
      />

      {recurringDelete && event && (
        <Modal
          isOpen
          onClose={() => setRecurringDelete(false)}
          title="반복 일정 삭제"
          size="sm"
        >
          <p className="text-sm text-slate-600 mb-3">'{event.title}' 반복 일정에서 어느 회차를 삭제할까요?</p>
          <div className="flex flex-col gap-2">
            {([
              ['this', '이 일정만'],
              ['following', '이 일정과 이후 일정 모두'],
              ['all', '모든 일정'],
            ] as Array<['this' | 'following' | 'all', string]>).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                disabled={busy}
                onClick={() => doDelete(mode)}
                className="h-10 px-3 rounded-lg bg-white border border-brand-600 text-brand-600 hover:bg-brand-50 text-sm font-semibold text-left disabled:opacity-50"
              >
                {label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setRecurringDelete(false)}
              className="h-10 px-3 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100"
            >
              취소
            </button>
          </div>
        </Modal>
      )}

      {sharing && event && occ && (
        <ForwardTargetModal
          sourceMessages={[] as Message[]}
          channels={channels}
          currentUser={currentUser}
          title="대화방에 공유"
          confirmLabel="공유"
          previewText={`${event.title} · ${formatOccurrence(occ.start, occ.end, event.allDay)}`}
          onConfirm={doShare}
          onClose={() => setSharing(false)}
        />
      )}
    </>
  );
};
